import { useEffect, useId, useState, type ReactNode, type RefObject } from 'react'

interface LensOptions {
  /** Raggio degli angoli in px (999 = capsula). */
  radius: number
  /** Ampiezza della fascia di bordo che rifrange, in px. */
  depth?: number
  /** Spostamento massimo ai bordi, in px. */
  strength?: number
}

/**
 * Filtri SVG dentro backdrop-filter: solo Chromium. Solo con mouse (su touch costa troppo a ogni scroll) e mai con
 * trasparenze ridotte. Altrove resta il vetro sfocato di `.glass`.
 */
function supportsLens(): boolean {
  return (
    'userAgentData' in navigator &&
    window.matchMedia('(pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-transparency: reduce)').matches
  )
}

/** Mappa di spostamento: rosso = asse x, blu = asse y; il grigio neutro al centro non sposta, i bordi sì. */
function displacementMap(w: number, h: number, radius: number, depth: number): string {
  const inner = Math.max(Math.min(radius, w / 2, h / 2) - depth, 0)
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    '<defs><linearGradient id="x"><stop offset="0" stop-color="#f00"/><stop offset="1" stop-color="#000"/></linearGradient>' +
    '<linearGradient id="y" x2="0" y2="1"><stop offset="0" stop-color="#000"/><stop offset="1" stop-color="#00f"/></linearGradient>' +
    `<filter id="b"><feGaussianBlur stdDeviation="${depth / 2}"/></filter></defs>` +
    `<rect width="${w}" height="${h}" fill="url(#x)"/>` +
    `<rect width="${w}" height="${h}" fill="url(#y)" style="mix-blend-mode:screen"/>` +
    `<rect x="${depth}" y="${depth}" width="${w - 2 * depth}" height="${h - 2 * depth}" rx="${inner}" fill="#808080" filter="url(#b)"/>` +
    '</svg>'
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/**
 * Rifrazione ai bordi di una lastra `.glass`: misura l'elemento, genera una mappa di spostamento della sua forma e
 * la collega con `--lens` (letta da backdrop-filter in glass.css). Restituisce il filtro da rendere dentro l'elemento.
 */
export function useLiquidLens(ref: RefObject<HTMLElement | null>, { radius, depth = 16, strength = 70 }: LensOptions): ReactNode {
  const id = `lens-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !supportsLens()) return
    const observer = new ResizeObserver(([entry]) => {
      const [box] = entry.borderBoxSize
      setSize({ w: Math.round(box.inlineSize), h: Math.round(box.blockSize) })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])

  const isUsable = size !== null && size.w > depth * 2 && size.h > depth * 2

  useEffect(() => {
    const el = ref.current
    if (!el || !isUsable) return
    el.style.setProperty('--lens', `url(#${id})`)
    el.dataset.lens = ''
    return () => {
      el.style.removeProperty('--lens')
      delete el.dataset.lens
    }
  }, [ref, id, isUsable])

  if (!isUsable) return null
  return (
    <svg aria-hidden="true" width="0" height="0" className="pointer-events-none absolute">
      <filter id={id} colorInterpolationFilters="sRGB">
        <feImage href={displacementMap(size.w, size.h, radius, depth)} x="0" y="0" width={size.w} height={size.h} result="map" />
        <feDisplacementMap in="SourceGraphic" in2="map" scale={-strength} xChannelSelector="R" yChannelSelector="B" />
      </filter>
    </svg>
  )
}
